import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  FiActivity,
  FiBarChart2,
  FiCalendar,
  FiClock,
  FiDownload,
  FiFilter,
  FiPackage,
  FiRefreshCw,
  FiSearch,
  FiSliders,
  FiTrendingDown,
  FiTrendingUp,
} from "react-icons/fi";
import * as XLSX from "xlsx";

const API_BASE_URL = (
  process.env.REACT_APP_POS_API_BASE_URL || `${process.env.REACT_APP_API_BASE_URL}`
).replace(/\/+$/, "");

const buildApiUrl = (path, query = "") => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}${query ? `?${query}` : ""}`;
};

const TAB_CONFIG = {
  realtime: {
    label: "Tồn realtime",
    endpoint: "/api/pos-inventory/realtime",
    icon: <FiActivity />,
    description: "Snapshot tồn hiện tại từ POS_InventorySnapshot",
  },
  asOf: {
    label: "Theo thời điểm",
    endpoint: "/api/pos-inventory/as-of",
    icon: <FiClock />,
    description: "Tính tồn kho từ ledger đến thời điểm đã chọn",
  },
  ledger: {
    label: "Phát sinh",
    endpoint: "/api/pos-inventory/ledger",
    icon: <FiSliders />,
    description: "Lịch sử nhập, xuất, kiểm kê từ POS_InventoryLedger",
  },
  report: {
    label: "Báo cáo XNT",
    endpoint: "/api/pos-inventory/report",
    icon: <FiBarChart2 />,
    description: "Tồn đầu, nhập, xuất, kiểm kê, tồn cuối theo kỳ",
  },
};

const MOVEMENT_TYPE_OPTIONS = [
  { value: "SALE", label: "SALE - Bán hàng" },
  { value: "SALE_BOM", label: "SALE_BOM - Xuất BOM bán hàng" },
  { value: "IMPORT", label: "IMPORT - Nhập kho" },
  { value: "TRANSFER_IN", label: "TRANSFER_IN - Nhận điều chuyển" },
  { value: "TRANSFER_OUT", label: "TRANSFER_OUT - Xuất điều chuyển" },
  { value: "STOCKTAKE", label: "STOCKTAKE - Kiểm kê" },
  { value: "STOCKTAKE_INCREASE", label: "STOCKTAKE_INCREASE - Tăng kiểm kê" },
  { value: "STOCKTAKE_DECREASE", label: "STOCKTAKE_DECREASE - Giảm kiểm kê" },
  { value: "ADJUSTMENT", label: "ADJUSTMENT - Điều chỉnh" },
];

const TABLE_PAGE_SIZE = 200;

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const todayText = formatDateInput(new Date());
const startOfMonthText = formatDateInput(
  new Date(new Date().getFullYear(), new Date().getMonth(), 1),
);

const formatNumber = (value) =>
  new Intl.NumberFormat("vi-VN", {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const toInventoryNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const normalizeInventoryReportRow = (item = {}) => {
  const tongXuatBanSource = item.TongXuatBan ?? item.tongXuatBan;
  const tongXuatBan = toInventoryNumber(tongXuatBanSource);
  const tongXuat = toInventoryNumber(item.TongXuat ?? item.tongXuat);
  const derivedTongXuatBanhHuy =
    tongXuatBanSource !== undefined ? Math.max(0, tongXuat - tongXuatBan) : 0;

  return {
    ...item,
    TongXuatBan: tongXuatBan,
    TongXuatBanhHuy: toInventoryNumber(
      item.TongXuatBanhHuy ??
        item.tongXuatBanhHuy ??
        item.TongHuy ??
        derivedTongXuatBanhHuy,
    ),
  };
};

const formatDateTime = (value) => {
  // Preserve the original timestamp returned by the API without
  // applying any timezone conversion or parsing side-effects.
  if (!value) return "-";
  return String(value).trim();
};

const toDateTimeLocalValue = (date) => {
  // Keep the ISO timestamp as-is (no timezone offset adjustment).
  return date.toISOString().slice(0, 16);
};

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getProductName = (item) => item.Ten_SP || item.TenSP || item.TenSanPham || "";

const getStoreId = (store) =>
  String(
    store?.MaCH ||
      store?.IDCH ||
      store?.Ma_CH ||
      store?.MaCuaHang ||
      store?.Ma_CuaHang ||
      store?.ID ||
      store?.Id ||
      store?.id ||
      "",
  ).trim();

const getStoreName = (store) =>
  store?.TenCH ||
  store?.TenCuaHang ||
  store?.Name ||
  store?.name ||
  (getStoreId(store) ? `Cửa hàng ${getStoreId(store)}` : "");

const getStoreAddress = (store) =>
  store?.DiaChi || store?.DiaChiCH || store?.Address || store?.address || "";

const getStoreOptionLabel = (store) => {
  const storeId = getStoreId(store);
  const storeName = getStoreName(store);
  return storeId && storeName ? `${storeId} - ${storeName}` : storeName || storeId;
};

const getQuantityClass = (value) => {
  const numericValue = Number(value || 0);
  if (numericValue > 0) return "text-success";
  if (numericValue < 0) return "text-danger";
  return "text-muted";
};

const buildQuery = (params) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      queryParams.append(key, String(value).trim());
    }
  });
  return queryParams.toString();
};

const downloadCsv = (filename, rows) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csvRows = [
    headers.join(","),
    ...rows.map((row) =>
      headers
        .map((header) => {
          const rawValue = row[header] ?? "";
          const value = String(rawValue).replace(/"/g, '""');
          return `"${value}"`;
        })
        .join(","),
    ),
  ];

  const blob = new Blob(["\ufeff" + csvRows.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const LEDGER_SUMMARY_HEADERS = [
  "Mã SP",
  "Sản phẩm",
  "ĐVT",
  "Mã NLSP",
  "Tổng số lượng",
];

export const summarizeLedgerRowsByMovementType = (rows = [], movementType) => {
  const normalizedMovementType = String(movementType || "")
    .trim()
    .toUpperCase();
  const summaryByProduct = new Map();

  rows.forEach((item) => {
    if (
      String(item.MovementType || "")
        .trim()
        .toUpperCase() !== normalizedMovementType
    ) {
      return;
    }

    const productId = String(item.MaSP || "").trim();
    if (!productId) return;

    const current = summaryByProduct.get(productId) || {
      "Mã SP": productId,
      "Sản phẩm": getProductName(item),
      "ĐVT": item.DVT_SP || "",
      "Mã NLSP": item.MaNLSP || "",
      "Tổng số lượng": 0,
    };

    current["Sản phẩm"] = current["Sản phẩm"] || getProductName(item);
    current["ĐVT"] = current["ĐVT"] || item.DVT_SP || "";
    current["Mã NLSP"] = current["Mã NLSP"] || item.MaNLSP || "";
    current["Tổng số lượng"] += Math.abs(toInventoryNumber(item.Quantity));
    summaryByProduct.set(productId, current);
  });

  return Array.from(summaryByProduct.values()).sort((left, right) =>
    String(left["Mã SP"]).localeCompare(String(right["Mã SP"]), "vi", {
      numeric: true,
      sensitivity: "base",
    }),
  );
};

const createSummaryWorksheet = (rows, movementType) => {
  const summaryRows = summarizeLedgerRowsByMovementType(rows, movementType);
  const worksheet = XLSX.utils.aoa_to_sheet([LEDGER_SUMMARY_HEADERS]);

  XLSX.utils.sheet_add_json(worksheet, summaryRows, {
    header: LEDGER_SUMMARY_HEADERS,
    origin: "A2",
    skipHeader: true,
  });
  worksheet["!cols"] = [
    { wch: 16 },
    { wch: 42 },
    { wch: 12 },
    { wch: 16 },
    { wch: 18 },
  ];
  worksheet["!autofilter"] = { ref: worksheet["!ref"] };

  return worksheet;
};

export const createLedgerExportWorkbook = (rows = []) => {
  const workbook = XLSX.utils.book_new();
  const detailWorksheet = XLSX.utils.json_to_sheet(rows);
  const detailHeaders = Object.keys(rows[0] || {});

  detailWorksheet["!cols"] = detailHeaders.map((header) => ({
    wch: Math.min(42, Math.max(12, header.length + 2)),
  }));
  if (detailWorksheet["!ref"]) {
    detailWorksheet["!autofilter"] = { ref: detailWorksheet["!ref"] };
  }

  XLSX.utils.book_append_sheet(workbook, detailWorksheet, "Phát sinh");
  XLSX.utils.book_append_sheet(
    workbook,
    createSummaryWorksheet(rows, "SALE"),
    "SALE",
  );
  XLSX.utils.book_append_sheet(
    workbook,
    createSummaryWorksheet(rows, "SALE_BOM"),
    "SALE_BOM",
  );

  return workbook;
};

const downloadLedgerWorkbook = (filename, rows) => {
  if (!rows.length) return;
  XLSX.writeFile(createLedgerExportWorkbook(rows), filename);
};

const SummaryCard = ({ icon, label, value, tone = "primary" }) => (
  <div className={`pos-inv-summary-card pos-inv-summary-${tone}`}>
    <div className="pos-inv-summary-icon">{icon}</div>
    <div>
      <p>{label}</p>
      <strong>{value}</strong>
    </div>
  </div>
);

const EmptyState = ({ activeTab }) => (
  <div className="pos-inv-empty">
    <FiPackage size={44} />
    <h5>Chưa có dữ liệu</h5>
    <p>{TAB_CONFIG[activeTab].description}</p>
  </div>
);

const PosInventoryPage = () => {
  const [activeTab, setActiveTab] = useState("realtime");
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [asOfResponse, setAsOfResponse] = useState("");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    MaCH: localStorage.getItem("MaCH") || "",
    MaSP: "",
    NgayBatDau: startOfMonthText,
    NgayKetThuc: todayText,
    asOf: toDateTimeLocalValue(new Date()),
    MovementTypes: [],
  });

  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const isHcmAdmin = localStorage.getItem("HCM_ADMIN") === "true";
  const isBakery = localStorage.getItem("isBakery") === "true";
  const userStoreId = String(localStorage.getItem("IDCH") || localStorage.getItem("MaCH") || "").trim();

  const filteredStores = useMemo(() => {
    if (isAdmin && String(localStorage.getItem("LoaiCH")) === "99") {
      return stores;
    }

    if (isHcmAdmin) {
      return stores.filter((store) => store.LoaiCH === 3 || store.LoaiCH === "3");
    }

    if (isBakery) {
      return stores.filter((store) => String(getStoreId(store)) === userStoreId);
    }

    return stores;
  }, [isAdmin, isBakery, isHcmAdmin, stores, userStoreId]);

  useEffect(() => {
    const fetchStores = async () => {
      setStoresLoading(true);
      try {
        const response = await fetch(buildApiUrl("/stores"));
        if (!response.ok) return;
        const result = await response.json();
        setStores(Array.isArray(result) ? result : result.data || []);
      } catch (err) {
        console.warn("Không thể tải danh sách cửa hàng:", err);
      } finally {
        setStoresLoading(false);
      }
    };

    fetchStores();
  }, []);

  const selectedStore = useMemo(
    () =>
      filteredStores.find(
        (store) => getStoreId(store) === String(filters.MaCH),
      ),
    [filteredStores, filters.MaCH],
  );

  const visibleRows = useMemo(() => {
    const keyword = normalizeText(searchText);
    if (!keyword) return data;

    return data.filter((item) => {
      const searchable = [
        item.MaSP,
        getProductName(item),
        item.DVT_SP,
        item.MaNLSP,
        item.MovementType,
        item.RefType,
        item.RefId,
        item.Note,
        item.CreatedBy,
      ]
        .map(normalizeText)
        .join(" ");
      return searchable.includes(keyword);
    });
  }, [data, searchText]);

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / TABLE_PAGE_SIZE));
  const displayedPage = Math.min(currentPage, totalPages);
  const tableRows = useMemo(() => {
    const startIndex = (displayedPage - 1) * TABLE_PAGE_SIZE;
    return visibleRows.slice(startIndex, startIndex + TABLE_PAGE_SIZE);
  }, [displayedPage, visibleRows]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, data, searchText]);

  const computedSummary = useMemo(() => {
    const totalQuantity = visibleRows.reduce(
      (sum, item) => sum + Number(item.Quantity || item.TonCuoiKy || 0),
      0,
    );
    const totalImport = visibleRows.reduce(
      (sum, item) =>
        sum + Number((item.TongNhapSyncMNPhanBo ?? item.TongNhap) || 0),
      0,
    );
    const totalExport = visibleRows.reduce(
      (sum, item) => {
        const reportExport =
          item.TongXuatBan !== undefined || item.TongXuatBanhHuy !== undefined
            ? Number(item.TongXuatBan || 0) + Number(item.TongXuatBanhHuy || 0)
            : Number(item.TongXuat || 0);
        return sum + reportExport;
      },
      0,
    );
    const netQuantity = visibleRows.reduce(
      (sum, item) => sum + Number(item.Quantity || 0),
      0,
    );

    return {
      totalRows: visibleRows.length,
      totalQuantity,
      totalImport,
      totalExport,
      netQuantity,
    };
  }, [visibleRows]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectAllOnFocus = (event) => {
    event.target.select();
  };

  const getCurrentRequest = () => {
    if (!filters.MaCH) {
      throw new Error("Vui lòng chọn hoặc nhập mã cửa hàng.");
    }

    if (activeTab === "realtime") {
      return {
        endpoint: TAB_CONFIG.realtime.endpoint,
        params: {
          MaCH: filters.MaCH,
          MaSP: filters.MaSP,
        },
      };
    }

    if (activeTab === "asOf") {
      if (!filters.asOf) {
        throw new Error("Vui lòng chọn thời điểm cần xem tồn kho.");
      }
      return {
        endpoint: TAB_CONFIG.asOf.endpoint,
        params: {
          MaCH: filters.MaCH,
          MaSP: filters.MaSP,
          asOf: filters.asOf,
        },
      };
    }

    if (!filters.NgayBatDau || !filters.NgayKetThuc) {
      throw new Error("Vui lòng chọn ngày bắt đầu và ngày kết thúc.");
    }

    if (filters.NgayBatDau > filters.NgayKetThuc) {
      throw new Error("Ngày bắt đầu không được lớn hơn ngày kết thúc.");
    }

    if (activeTab === "ledger") {
      return {
        endpoint: TAB_CONFIG.ledger.endpoint,
        params: {
          MaCH: filters.MaCH,
          MaSP: filters.MaSP,
          NgayBatDau: filters.NgayBatDau,
          NgayKetThuc: filters.NgayKetThuc,
        },
      };
    }

    return {
      endpoint: TAB_CONFIG.report.endpoint,
      params: {
        MaCH: filters.MaCH,
        MaSP: filters.MaSP,
        NgayBatDau: filters.NgayBatDau,
        NgayKetThuc: filters.NgayKetThuc,
      },
    };
  };

  const fetchInventoryData = async () => {
    setError("");

    let request;
    try {
      request = getCurrentRequest();
    } catch (err) {
      setError(err.message);
      return;
    }

    setLoading(true);
    try {
      const selectedMovementTypes =
        activeTab === "ledger" && Array.isArray(filters.MovementTypes)
          ? filters.MovementTypes
          : [];
      const movementTypesToRequest = selectedMovementTypes.length
        ? selectedMovementTypes
        : [""];

      const results = await Promise.all(
        movementTypesToRequest.map(async (movementType) => {
          const query = buildQuery({
            ...request.params,
            MovementType: movementType,
          });
          const response = await fetch(buildApiUrl(request.endpoint, query));
          const result = await response.json();

          if (!response.ok || result.success === false) {
            throw new Error(result.message || "Không thể tải dữ liệu kho POS.");
          }

          return result;
        }),
      );

      const combinedData = results
        .flatMap((result) => (Array.isArray(result.data) ? result.data : []))
        .map((item) =>
          activeTab === "report" ? normalizeInventoryReportRow(item) : item,
        );
      if (activeTab === "ledger" && results.length > 1) {
        combinedData.sort((left, right) =>
          String(right.CreatedAt || "").localeCompare(String(left.CreatedAt || "")),
        );
      }
      const combinedSummary =
        activeTab === "ledger" && results.length > 1
          ? {
              ...(results[0].summary || {}),
              totalRows: combinedData.length,
              netQuantity: combinedData.reduce(
                (sum, item) => sum + Number(item.Quantity || 0),
                0,
              ),
            }
          : results[0].summary || null;

      setData(combinedData);
      setSummary(combinedSummary);
      setAsOfResponse(results[0].asOf || "");
    } catch (err) {
      setData([]);
      setSummary(null);
      setAsOfResponse("");
      setError(err.message || "Lỗi kết nối server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData([]);
    setSummary(null);
    setAsOfResponse("");
    setError("");
    setSearchText("");
  }, [activeTab]);

  const exportCurrentRows = () => {
    const suffix = `${activeTab}_${filters.MaCH || "all"}_${Date.now()}`;

    if (activeTab === "ledger") {
      downloadLedgerWorkbook(`pos_inventory_${suffix}.xlsx`, visibleRows);
      return;
    }

    downloadCsv(`pos_inventory_${suffix}.csv`, visibleRows);
  };

  const renderProductColumns = (item) => (
    <>
      <td className="fw-semibold text-nowrap">{item.MaSP}</td>
      <td>
        <div className="pos-inv-product-name">{getProductName(item) || "-"}</div>
        <div className="small text-muted">{item.MaNLSP || "Chưa phân loại"}</div>
      </td>
      <td className="text-nowrap">{item.DVT_SP || "-"}</td>
    </>
  );

  const renderTable = () => {
    if (loading) {
      return (
        <div className="pos-inv-loading">
          <CircularProgress size={34} />
          <span>Đang tải dữ liệu kho...</span>
        </div>
      );
    }

    if (!visibleRows.length) {
      return <EmptyState activeTab={activeTab} />;
    }

    if (activeTab === "ledger") {
      return (
        <div className="table-responsive pos-inv-table-wrap">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Thời gian</th>
                <th>Mã SP</th>
                <th>Sản phẩm</th>
                <th>ĐVT</th>
                <th className="text-end">SL</th>
                <th>Loại</th>
                <th>Chứng từ</th>
                <th>Ghi chú</th>
                <th>Người tạo</th>
                <th>Ngày KD</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((item) => (
                <tr key={item.Id || `${item.MaSP}-${item.CreatedAt}-${item.RefId}`}>
                  <td className="text-nowrap">{formatDateTime(item.CreatedAt)}</td>
                  {renderProductColumns(item)}
                  <td className={`text-end fw-bold ${getQuantityClass(item.Quantity)}`}>
                    {formatNumber(item.Quantity)}
                  </td>
                  <td>
                    <Chip
                      size="small"
                      label={item.MovementType || "-"}
                      color={Number(item.Quantity || 0) >= 0 ? "success" : "error"}
                      variant="outlined"
                    />
                  </td>
                  <td>
                    <div className="small fw-semibold">{item.RefType || "-"}</div>
                    <div className="small text-muted">{item.RefId || "-"}</div>
                  </td>
                  <td className="pos-inv-note">{item.Note || "-"}</td>
                  <td>{item.CreatedBy || "-"}</td>
                  <td className="text-nowrap">{item.BizDate ? String(item.BizDate).slice(0, 10) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (activeTab === "report") {
      return (
        <div className="table-responsive pos-inv-table-wrap">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Mã SP</th>
                <th>Sản phẩm</th>
                <th>ĐVT</th>
                <th className="text-end">Tồn đầu</th>
                <th className="text-end">Nhập</th>
                <th className="text-end">Xuất bán</th>
                <th className="text-end">Xuất hủy</th>
                <th className="text-end">CL kiểm kê</th>
                <th className="text-end">Tồn cuối</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((item) => (
                <tr key={`${item.MaCH}-${item.MaSP}`}>
                  {renderProductColumns(item)}
                  <td className="text-end">{formatNumber(item.TonDauKy)}</td>
                  <td className="text-end text-success fw-semibold">
                    {formatNumber(item.TongNhapSyncMNPhanBo)}
                  </td>
                  <td className="text-end text-danger fw-semibold">
                    {formatNumber(item.TongXuatBan)}
                  </td>
                  <td className="text-end text-danger fw-semibold">
                    {formatNumber(item.TongXuatBanhHuy)}
                  </td>
                  <td className={`text-end fw-semibold ${getQuantityClass(item.ChenhLechKiemKe)}`}>
                    {formatNumber(item.ChenhLechKiemKe)}
                  </td>
                  <td className={`text-end fw-bold ${getQuantityClass(item.TonCuoiKy)}`}>
                    {formatNumber(item.TonCuoiKy)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div className="table-responsive pos-inv-table-wrap">
        <table className="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>Mã SP</th>
              <th>Sản phẩm</th>
              <th>ĐVT</th>
              <th className="text-end">Tồn kho</th>
              <th>Cập nhật gần nhất</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((item) => (
              <tr key={`${item.MaCH}-${item.MaSP}`}>
                {renderProductColumns(item)}
                <td className={`text-end fw-bold ${getQuantityClass(item.Quantity)}`}>
                  {formatNumber(item.Quantity)}
                </td>
                <td className="text-nowrap">
                  {activeTab === "asOf"
                    ? formatDateTime(item.LastMovementAt)
                    : formatDateTime(item.UpdatedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const summaryCards = () => {
    if (activeTab === "report") {
      return (
        <>
          <SummaryCard
            icon={<FiPackage />}
            label="Mặt hàng"
            value={formatNumber(summary?.totalItems ?? computedSummary.totalRows)}
          />
          <SummaryCard
            icon={<FiTrendingUp />}
            label="Tổng nhập"
            value={formatNumber(computedSummary.totalImport)}
            tone="success"
          />
          <SummaryCard
            icon={<FiTrendingDown />}
            label="Tổng xuất"
            value={formatNumber(computedSummary.totalExport)}
            tone="danger"
          />
          <SummaryCard
            icon={<FiBarChart2 />}
            label="Tồn cuối"
            value={formatNumber(summary?.totalTonCuoiKy ?? computedSummary.totalQuantity)}
            tone="info"
          />
        </>
      );
    }

    if (activeTab === "ledger") {
      return (
        <>
          <SummaryCard
            icon={<FiSliders />}
            label="Dòng phát sinh"
            value={formatNumber(summary?.totalRows ?? computedSummary.totalRows)}
          />
          <SummaryCard
            icon={<FiBarChart2 />}
            label="Net quantity"
            value={formatNumber(summary?.netQuantity ?? computedSummary.netQuantity)}
            tone={(summary?.netQuantity ?? computedSummary.netQuantity) >= 0 ? "success" : "danger"}
          />
          <SummaryCard
            icon={<FiCalendar />}
            label="Từ ngày"
            value={filters.NgayBatDau || "-"}
            tone="muted"
          />
          <SummaryCard
            icon={<FiCalendar />}
            label="Đến ngày"
            value={filters.NgayKetThuc || "-"}
            tone="muted"
          />
        </>
      );
    }

    return (
      <>
        <SummaryCard
          icon={<FiPackage />}
          label="Mặt hàng"
          value={formatNumber(summary?.totalItems ?? computedSummary.totalRows)}
        />
        <SummaryCard
          icon={<FiBarChart2 />}
          label={activeTab === "asOf" ? "Tổng tồn tại thời điểm" : "Tổng tồn realtime"}
          value={formatNumber(summary?.totalQuantity ?? computedSummary.totalQuantity)}
          tone="info"
        />
        <SummaryCard
          icon={<FiClock />}
          label={activeTab === "asOf" ? "Thời điểm xem" : "Cửa hàng"}
          value={
            activeTab === "asOf"
              ? formatDateTime(asOfResponse || filters.asOf)
              : filters.MaCH || "-"
          }
          tone="muted"
        />
        <SummaryCard
          icon={<FiFilter />}
          label="Đang hiển thị"
          value={formatNumber(computedSummary.totalRows)}
          tone="muted"
        />
      </>
    );
  };

  return (
    <div className="pos-inv-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .pos-inv-page {
          min-height: 100vh;
          background: #f5f7fb;
          color: #172033;
          font-family: "Inter", "Segoe UI", Arial, sans-serif;
          padding: 24px;
        }

        .pos-inv-hero {
          background: #ffffff;
          border: 1px solid #e3e8f2;
          border-radius: 8px;
          padding: 22px;
          display: flex;
          justify-content: space-between;
          gap: 18px;
          align-items: center;
          box-shadow: 0 10px 28px rgba(20, 33, 61, 0.06);
        }

        .pos-inv-title {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .pos-inv-title-icon {
          width: 46px;
          height: 46px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #0f5db8;
          background: #e8f2ff;
          font-size: 24px;
        }

        .pos-inv-title h1 {
          font-size: 24px;
          line-height: 1.25;
          margin: 0;
          font-weight: 800;
          letter-spacing: 0;
        }

        .pos-inv-title p {
          margin: 4px 0 0;
          color: #64748b;
          font-size: 14px;
        }

        .pos-inv-filter-card,
        .pos-inv-data-card {
          background: #ffffff;
          border: 1px solid #e3e8f2;
          border-radius: 8px;
          box-shadow: 0 8px 22px rgba(20, 33, 61, 0.045);
        }

        .pos-inv-filter-card {
          padding: 18px;
        }

        .pos-inv-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #26364d;
          margin-bottom: 14px;
        }

        .pos-inv-tabs {
          border-bottom: 1px solid #e8edf5;
        }

        .pos-inv-tab-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-height: 24px;
        }

        .pos-inv-summary-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
        }

        .pos-inv-summary-card {
          min-height: 92px;
          border: 1px solid #e6ebf3;
          border-radius: 8px;
          background: #ffffff;
          padding: 14px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .pos-inv-summary-icon {
          width: 38px;
          height: 38px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          background: #e8f2ff;
          color: #0f5db8;
          flex: 0 0 auto;
        }

        .pos-inv-summary-card p {
          margin: 0 0 5px;
          color: #64748b;
          font-size: 12px;
          font-weight: 600;
        }

        .pos-inv-summary-card strong {
          display: block;
          color: #162033;
          font-size: 20px;
          line-height: 1.15;
        }

        .pos-inv-summary-success .pos-inv-summary-icon {
          background: #e7f7ef;
          color: #168449;
        }

        .pos-inv-summary-danger .pos-inv-summary-icon {
          background: #fdecec;
          color: #c3362b;
        }

        .pos-inv-summary-info .pos-inv-summary-icon {
          background: #e9f7fb;
          color: #087990;
        }

        .pos-inv-summary-muted .pos-inv-summary-icon {
          background: #f0f3f8;
          color: #64748b;
        }

        .pos-inv-data-header {
          padding: 16px 18px;
          border-bottom: 1px solid #e8edf5;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
        }

        .pos-inv-data-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 800;
        }

        .pos-inv-data-header p {
          margin: 3px 0 0;
          color: #64748b;
          font-size: 13px;
        }

        .pos-inv-toolbar {
          padding: 14px 18px;
          border-bottom: 1px solid #e8edf5;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
        }

        .pos-inv-table-wrap {
          max-height: 58vh;
          overflow: auto;
        }

        .pos-inv-table-wrap thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f8fafc;
          color: #475569;
          border-bottom: 1px solid #dfe6f0;
          font-size: 12px;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .pos-inv-table-wrap tbody td {
          font-size: 13px;
          vertical-align: middle;
        }

        .pos-inv-pagination {
          padding: 12px 18px;
          border-top: 1px solid #e8edf5;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .pos-inv-product-name {
          min-width: 220px;
          max-width: 360px;
          color: #172033;
          font-weight: 600;
        }

        .pos-inv-note {
          min-width: 170px;
          max-width: 280px;
          white-space: normal;
        }

        .pos-inv-loading,
        .pos-inv-empty {
          min-height: 290px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: #64748b;
          text-align: center;
          padding: 28px;
        }

        .pos-inv-empty h5 {
          margin: 4px 0 0;
          color: #26364d;
          font-weight: 800;
        }

        .pos-inv-empty p {
          margin: 0;
          max-width: 440px;
        }

        .pos-inv-actions {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }

        .pos-inv-store-note {
          color: #64748b;
          font-size: 12px;
          margin-top: 5px;
        }

        @media (max-width: 992px) {
          .pos-inv-page {
            padding: 14px;
          }

          .pos-inv-hero,
          .pos-inv-data-header,
          .pos-inv-toolbar {
            align-items: stretch;
            flex-direction: column;
          }

          .pos-inv-summary-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 576px) {
          .pos-inv-summary-grid {
            grid-template-columns: 1fr;
          }

          .pos-inv-title {
            align-items: flex-start;
          }

          .pos-inv-title h1 {
            font-size: 20px;
          }
        }
      `}</style>

      <div className="pos-inv-hero mb-3">
        <div className="pos-inv-title">
          <span className="pos-inv-title-icon">
            <FiPackage />
          </span>
          <div>
            <h1>Kho hàng POS</h1>
            <p>Theo dõi tồn realtime, tồn theo thời điểm, phát sinh và báo cáo XNT theo ledger.</p>
          </div>
        </div>
        <div className="pos-inv-actions">
          <Tooltip title="Tải lại dữ liệu theo tab và bộ lọc hiện tại">
            <span>
              <button
                type="button"
                className="btn btn-primary d-inline-flex align-items-center gap-2"
                onClick={fetchInventoryData}
                disabled={loading}
              >
                {loading ? <CircularProgress color="inherit" size={16} /> : <FiRefreshCw />}
                Tải dữ liệu
              </button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              activeTab === "ledger"
                ? "Xuất chi tiết và tổng hợp SALE, SALE_BOM ra Excel"
                : "Xuất các dòng đang hiển thị ra CSV"
            }
          >
            <span>
              <button
                type="button"
                className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                onClick={exportCurrentRows}
                disabled={!visibleRows.length}
              >
                <FiDownload />
                {activeTab === "ledger" ? "Xuất Excel" : "Xuất CSV"}
              </button>
            </span>
          </Tooltip>
        </div>
      </div>

      {error && (
        <Alert severity="error" className="mb-3" onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <div className="pos-inv-filter-card mb-3">
        <div className="pos-inv-section-title">
          <FiFilter />
          Bộ lọc dữ liệu
        </div>

        <Box className="pos-inv-tabs mb-3">
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {Object.entries(TAB_CONFIG).map(([key, config]) => (
              <Tab
                key={key}
                value={key}
                label={
                  <span className="pos-inv-tab-label">
                    {config.icon}
                    {config.label}
                  </span>
                }
              />
            ))}
          </Tabs>
        </Box>

        <div className="row g-3">
          <div className="col-12 col-lg-3">
            <label className="form-label fw-semibold">Cửa hàng</label>
            <Autocomplete
              size="small"
              options={filteredStores}
              value={selectedStore || null}
              loading={storesLoading}
              disabled={storesLoading}
              getOptionLabel={getStoreOptionLabel}
              isOptionEqualToValue={(option, value) =>
                getStoreId(option) === getStoreId(value)
              }
              filterOptions={(options, state) => {
                const keyword = normalizeText(state.inputValue);
                if (!keyword) return options;
                return options.filter((store) => {
                  const searchable = [
                    getStoreId(store),
                    getStoreName(store),
                    getStoreAddress(store),
                  ]
                    .map(normalizeText)
                    .join(" ");
                  return searchable.includes(keyword);
                });
              }}
              onChange={(_, store) => handleFilterChange("MaCH", getStoreId(store))}
              noOptionsText="Không tìm thấy cửa hàng"
              loadingText="Đang tải cửa hàng..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Gõ mã hoặc tên cửa hàng"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {storesLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <div className="pos-inv-store-note">
              {selectedStore
                ? getStoreAddress(selectedStore) || getStoreOptionLabel(selectedStore)
                : "Có thể nhập mã cửa hàng thủ công bên dưới nếu danh sách chưa có."}
            </div>
          </div>

          <div className="col-6 col-lg-2">
            <label className="form-label fw-semibold">Mã cửa hàng</label>
            <input
              className="form-control"
              value={filters.MaCH}
              onChange={(event) => handleFilterChange("MaCH", event.target.value)}
              placeholder="VD: 158"
            />
          </div>

          <div className="col-6 col-lg-2">
            <label className="form-label fw-semibold">Mã sản phẩm</label>
            <input
              className="form-control"
              value={filters.MaSP}
              onChange={(event) => handleFilterChange("MaSP", event.target.value)}
              placeholder="Để trống = tất cả"
            />
          </div>

          {activeTab === "asOf" ? (
            <div className="col-12 col-lg-3">
              <label className="form-label fw-semibold">Thời điểm</label>
              <input
                type="datetime-local"
                className="form-control"
                value={filters.asOf}
                onChange={(event) => handleFilterChange("asOf", event.target.value)}
              />
            </div>
          ) : (
            <>
              <div className="col-6 col-lg-2">
                <label className="form-label fw-semibold">Ngày bắt đầu</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.NgayBatDau}
                  onChange={(event) =>
                    handleFilterChange("NgayBatDau", event.target.value)
                  }
                  disabled={activeTab === "realtime"}
                />
              </div>
              <div className="col-6 col-lg-2">
                <label className="form-label fw-semibold">Ngày kết thúc</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.NgayKetThuc}
                  onChange={(event) =>
                    handleFilterChange("NgayKetThuc", event.target.value)
                  }
                  disabled={activeTab === "realtime"}
                />
              </div>
            </>
          )}

          {activeTab === "ledger" && (
            <div className="col-12 col-lg-3">
              <label className="form-label fw-semibold">Loại phát sinh</label>
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                limitTags={2}
                options={MOVEMENT_TYPE_OPTIONS}
                value={MOVEMENT_TYPE_OPTIONS.filter((option) =>
                  filters.MovementTypes.includes(option.value),
                )}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={(_, options) =>
                  handleFilterChange(
                    "MovementTypes",
                    options.map((option) => option.value),
                  )
                }
                noOptionsText="Không tìm thấy loại phát sinh"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      filters.MovementTypes.length ? "" : "Để trống = tất cả"
                    }
                  />
                )}
              />
            </div>
          )}
        </div>
      </div>

      <div className="pos-inv-summary-grid mb-3">{summaryCards()}</div>

      <div className="pos-inv-data-card">
        <div className="pos-inv-data-header">
          <div>
            <h2>{TAB_CONFIG[activeTab].label}</h2>
            <p>{TAB_CONFIG[activeTab].description}</p>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Chip
              size="small"
              label={`MaCH: ${filters.MaCH || "-"}`}
              variant="outlined"
            />
            {filters.MaSP && (
              <Chip size="small" label={`MaSP: ${filters.MaSP}`} variant="outlined" />
            )}
          </div>
        </div>

        <div className="pos-inv-toolbar">
          <TextField
            size="small"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onFocus={handleSelectAllOnFocus}
            placeholder="Tìm mã, tên sản phẩm, loại phát sinh..."
            sx={{ minWidth: { xs: "100%", sm: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiSearch />
                </InputAdornment>
              ),
            }}
          />
          <div className="small text-muted">
            Tìm thấy <strong>{formatNumber(visibleRows.length)}</strong> dòng
          </div>
        </div>

        {renderTable()}

        {visibleRows.length > TABLE_PAGE_SIZE && (
          <div className="pos-inv-pagination">
            <div className="small text-muted">
              Dòng {formatNumber((displayedPage - 1) * TABLE_PAGE_SIZE + 1)}–
              {formatNumber(
                Math.min(displayedPage * TABLE_PAGE_SIZE, visibleRows.length),
              )}{" "}
              / {formatNumber(visibleRows.length)}
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={displayedPage === 1}
              >
                Trước
              </button>
              <span className="small text-nowrap">
                Trang {formatNumber(displayedPage)} / {formatNumber(totalPages)}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
                disabled={displayedPage === totalPages}
              >
                Sau
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PosInventoryPage;
